import { useTranslation } from 'react-i18next'
import { ThumbsUp, ThumbsDown } from 'lucide-react'
import type { ListingAuditReport } from '@/lib/api/listing-builder.types'

interface StrengthsWeaknessTableProps {
  strengths: ListingAuditReport['strengths']
  weaknesses: ListingAuditReport['weaknesses']
}

const CLASS_COLORS: Record<string, string> = {
  primary: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  secondary: 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400',
  emerging: 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400',
  critical: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  improvement: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
  inconsistent: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
}

export function StrengthsWeaknessTable({ strengths, weaknesses }: StrengthsWeaknessTableProps) {
  const { t } = useTranslation()

  return (
    <div className="space-y-4">
      {strengths?.length > 0 && (
        <div className="bg-white dark:bg-neutral-800 rounded-xl border border-neutral-200 dark:border-neutral-700 overflow-hidden">
          <div className="px-5 py-4 border-b border-neutral-200 dark:border-neutral-700 flex items-center gap-2">
            <ThumbsUp className="h-4 w-4 text-green-500" />
            <h3 className="font-semibold text-neutral-900 dark:text-white">{t('amazonListing.report.strengths')}</h3>
          </div>
          <div className="divide-y divide-neutral-100 dark:divide-neutral-700">
            {strengths.map((s, i) => (
              <div key={i} className="px-5 py-3">
                <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mb-1">
                  <span className="text-sm font-medium text-neutral-900 dark:text-white">{s.strength}</span>
                  <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${CLASS_COLORS[s.classification] || ''}`}>{t(`amazonListing.report.class${s.classification.charAt(0).toUpperCase()}${s.classification.slice(1)}`, s.classification)}</span>
                </div>
                {s.analysis && <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">{s.analysis}</p>}
                {s.data_points?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {s.data_points.map((dp, k) => (
                      <span key={k} className="px-1.5 py-0.5 rounded text-xs bg-neutral-100 dark:bg-neutral-700 text-neutral-600 dark:text-neutral-300">{dp}</span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {weaknesses?.length > 0 && (
        <div className="bg-white dark:bg-neutral-800 rounded-xl border border-neutral-200 dark:border-neutral-700 overflow-hidden">
          <div className="px-5 py-4 border-b border-neutral-200 dark:border-neutral-700 flex items-center gap-2">
            <ThumbsDown className="h-4 w-4 text-red-500" />
            <h3 className="font-semibold text-neutral-900 dark:text-white">{t('amazonListing.report.weaknesses')}</h3>
          </div>
          <div className="divide-y divide-neutral-100 dark:divide-neutral-700">
            {weaknesses.map((w, i) => (
              <div key={i} className="px-5 py-3">
                <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mb-1">
                  <span className="px-1.5 py-0.5 rounded text-xs font-bold bg-neutral-200 dark:bg-neutral-600 text-neutral-700 dark:text-neutral-200">#{w.priority}</span>
                  <span className="text-sm font-medium text-neutral-900 dark:text-white">{w.weakness}</span>
                  <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${CLASS_COLORS[w.classification] || ''}`}>{t(`amazonListing.report.class${w.classification.charAt(0).toUpperCase()}${w.classification.slice(1)}`, w.classification)}</span>
                </div>
                {w.analysis && <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">{w.analysis}</p>}
                {w.data_points?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {w.data_points.map((dp, k) => (
                      <span key={k} className="px-1.5 py-0.5 rounded text-xs bg-neutral-100 dark:bg-neutral-700 text-neutral-600 dark:text-neutral-300">{dp}</span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
